import React from 'react';
import { School as LearningIcon } from '@mui/icons-material';
import { Box, Card, CardActionArea, CardContent, Chip,Divider, Stack, Typography } from '@mui/material';

import type { LearningRuleDocument } from '../types';

interface LearningRuleCardProps {
  rule: LearningRuleDocument;
  isGlobal: boolean;
  variant?: 'preview' | 'full';
  onClick?: () => void;
}

export const LearningRuleCard: React.FC<LearningRuleCardProps> = ({ 
  rule, 
  isGlobal, 
  variant = 'full',
  onClick 
}) => {
  const isPreview = variant === 'preview';

  const CardWrapper = ({ children }: { children: React.ReactNode }) => (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        bgcolor: 'background.paper',
        '&:hover': onClick ? {
          boxShadow: 1,
          borderColor: 'borderColors.brand',
          cursor: 'pointer'
        } : undefined,
      }}
    >
      {onClick ? (
        <CardActionArea onClick={onClick} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}>
          {children}
        </CardActionArea>
      ) : children}
    </Card>
  );

  return (
    <CardWrapper>
      {/* Header Section with Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          pb: 1,
          bgcolor: 'backgroundColors.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'secondary.main',
            color: 'white',
            flexShrink: 0,
          }}
        >
          <LearningIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={0.25}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: '0.8125rem',
                lineHeight: 1.25,
                color: 'textColors.darkText',
                // Clamp title lines in preview
                ...(isPreview && {
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                })
              }}
            >
              {rule.title}
            </Typography>
            {isGlobal && (
              <Chip 
                label="Global" 
                size="small" 
                sx={{ height: 16, fontSize: '0.625rem' }} 
                color="secondary"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="caption" sx={{ color: 'textColors.lightText', fontSize: '0.6875rem' }}>
            Learning Rule
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'borderColors.primary' }} />

      {/* Content Section */}
      <CardContent sx={{ p: 1.5, pt: 1, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', width: '100%' }}>
        <Stack spacing={1}>
          {/* Teaching Summary (Shortened for preview) */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.75rem', 
                lineHeight: 1.4, 
                color: 'textColors.darkText', 
                fontStyle: 'italic', 
                mb: 1,
                ...(isPreview && {
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                })
              }}
            >
              {rule.teaching_summary}
            </Typography>
          </Box>

          {!isPreview && (
            <>
              {/* When to Follow */}
              {rule.when_to_follow && (
                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      fontWeight: 600,
                      color: 'textColors.lightText',
                      fontSize: '0.625rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      display: 'block',
                      mb: 0.25,
                    }}
                  >
                    When to Follow
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: 'textColors.darkText' }}>
                    {rule.when_to_follow}
                  </Typography>
                </Box>
              )}

              {/* Recommended Actions */}
              {rule.recommended_actions && (
                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      fontWeight: 600,
                      color: 'textColors.lightText',
                      fontSize: '0.625rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      display: 'block',
                      mb: 0.25,
                    }}
                  >
                    Actions
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: 'textColors.darkText' }}>
                    {rule.recommended_actions}
                  </Typography>
                </Box>
              )}

              {/* Clinical Rationale */}
              {rule.clinical_rationale && (
                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      fontWeight: 600,
                      color: 'textColors.lightText',
                      fontSize: '0.625rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      display: 'block',
                      mb: 0.25,
                    }}
                  >
                    Clinical Rationale
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: 'textColors.darkText' }}>
                    {rule.clinical_rationale}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Stack>
      </CardContent>

      {/* Footer with Source Information */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          mt: 'auto',
          bgcolor: 'backgroundColors.secondary',
          borderTop: '1px solid',
          borderColor: 'borderColors.primary',
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'textColors.lightText', 
            fontSize: '0.6875rem', 
            fontStyle: 'italic',
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          Source: <strong style={{ color: 'inherit' }}>{rule.source_document || 'Unknown'}</strong>
          {rule.source_date && ` • ${rule.source_date}`}
        </Typography>
      </Box>
    </CardWrapper>
  );
};
